import React from 'react';
import Text from 'react-format-text';
import { CalendarDays, User, MapPin, ExternalLink, Reply, ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Card, CardContent } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader } from '../../ui/loader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';

import { ArticleLink, BoardLink, UserLink } from '../../Links';
import { CustomContainer, FollowingBtn, FollowingBtnGuide, ShareBtn } from '../../common';
import { formatTimestamp, formatTimestampWithTimezone } from '../../Utils';

const QUOTE_PREFIX = ['※', ':'];

const renderContent = (isMobile: boolean, content: string) => (
  <div>
    {content.split('\n').map((line, index) => {
      if (QUOTE_PREFIX.includes(line[0])) {
        return (
          <h3 key={index} className={`${isMobile ? 'text-base' : 'text-lg'} text-gray-600 my-2`}>
            {line}
          </h3>
        );
      }
      return (
        <div key={index}>
          <Text>
            {line}
          </Text>
          <br />
        </div>
      );
    })}
  </div>
);

const Statistics = ({
  loading,
  data,
}: {
  loading: boolean;
  data: any;
}) => loading ? (
  <div className="flex justify-center py-4">
    <Loader />
  </div>
) : (
  <div className="flex gap-6 float-right">
    <div className="text-center">
      <div className="text-3xl font-bold">{data.num_replies}</div>
      <div className="text-sm text-gray-600 flex items-center justify-center mt-1">
        回文數 <ArrowRight className="h-4 w-4 text-yellow-600 ml-1" />
      </div>
    </div>
    <div className="text-center">
      <div className="text-3xl font-bold">{data.num_hates}</div>
      <div className="text-sm text-gray-600 flex items-center justify-center mt-1">
        噓文數 <ThumbsDown className="h-4 w-4 text-red-600 ml-1" />
      </div>
    </div>
    <div className="text-center">
      <div className="text-3xl font-bold">{data.num_likes}</div>
      <div className="text-sm text-gray-600 flex items-center justify-center mt-1">
        推文數 <ThumbsUp className="h-4 w-4 text-blue-600 ml-1" />
      </div>
    </div>
  </div>
);

interface InfoProps {
  data: {
    active?: boolean;
    authorId: string;
    board: string | {
      id: string;
      alias?: string;
      title?: string;
    };
    city?: string;
    code2?: string;
    content: string;
    country?: string;
    datetime: string;
    id: string;
    isHidden?: boolean;
    title: string;
    updatedAt?: string;
    url: string;
  };
  replyTo?: any;
  pushesStats?: any;
  authed: boolean;
  isMobile: boolean;
}

export default (props: InfoProps) => {
  const {
    data: {
      authorId,
      board,
      city,
      code2,
      content,
      country,
      datetime,
      id,
      title,
      updatedAt,
      url,
      active,
      isHidden,
    },
    authed,
    isMobile,
    pushesStats,
    replyTo,
  } = props;

  return (
    <CustomContainer>
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-2">
              {authed ? (
                <FollowingBtn
                  platform="ptt"
                  type="article"
                  id={id}
                />
              ) : (
                <FollowingBtnGuide />
              )}
              <ShareBtn />
            </div>
            <BoardLink id={typeof board === 'string' ? board : board.id}>
              <Badge variant="default" className="bg-teal-600 hover:bg-teal-700">
                {typeof board === 'string' ? board : (board.alias ?? board.title)}
                {typeof board !== 'string' && board.title && ` (${board.title})`}
              </Badge>
            </BoardLink>
          </div>

          <div>
            {!isMobile && (
              <Statistics
                loading={pushesStats.isFetching}
                data={pushesStats.data}
              />
            )}
            
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold mb-4`}>
              {title}
            </h1>
            
            <div className="space-y-2 text-gray-600 mb-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <UserLink id={authorId}>
                        {authorId}
                      </UserLink>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>使用者ID</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>{formatTimestamp(datetime)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>發文時間</p>
                  </TooltipContent>
                </Tooltip>

                {code2 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {city} ({country}{' '}
                        <span className={`fi fi-${code2.toLowerCase()}`}></span>)
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>發文IP位置</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {replyTo && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Reply className="h-4 w-4" />
                        <ArticleLink id={replyTo.id}>
                          {replyTo.title}
                        </ArticleLink>
                        (<UserLink id={replyTo.author_id}>
                          {replyTo.author_id}
                        </UserLink>)
                        @ {formatTimestamp(replyTo.datetime)}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>回復原始文章</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>

            <div className={`${isMobile ? 'text-sm' : 'text-base'} leading-relaxed`}>
              {renderContent(isMobile, content)}
              {isHidden && (
                <Alert className="mt-4 bg-olive-50 border-olive-200">
                  <AlertDescription>
                    因特殊原因及原作者要求 無法顯示文章內容
                  </AlertDescription>
                </Alert>
              )}
              <br />
              <a
                className={`text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 ${
                  isMobile ? 'text-xs' : 'text-sm'
                }`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4" />
                原文連結
              </a>
            </div>

            {!active && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>
                  此文章已被刪除囉!!
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </CustomContainer>
  );
};